import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { adminApi, formatApiError } from "@/admin/api";
import { SERVICES } from "@/data/services";
import { inputCls, ServiceSelect, HomeSwitch } from "@/admin/components/PortfolioFields";

const blank = () => ({ title: "", service: SERVICES[0].slug, state: "USA", year: new Date().getFullYear(), show_on_home: true });

export function UploadForm({ onUploaded }) {
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a PDF file first");
    if (file.size > 40 * 1024 * 1024) return toast.error("PDF must be under 40 MB");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    setProgress(0);
    try {
      const { data } = await adminApi.post("/portfolio", fd, {
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      onUploaded(data.item);
      toast.success(`"${data.item.title}" added to portfolio`);
      setForm(blank());
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setProgress(null);
    }
  };

  const busy = progress !== null;
  return (
    <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="portfolio-upload-form">
      <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500">Add a new sample</div>
      <label className="flex items-center gap-4 border border-dashed border-zinc-300 p-5 cursor-pointer hover:border-[#0044FF] transition-colors">
        <UploadSimple size={24} className="text-[#0044FF]" />
        <span className="text-sm text-zinc-700" data-testid="portfolio-upload-file-name">
          {file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB` : "Choose a PDF drawing (max 40 MB)"}
        </span>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="portfolio-upload-file-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <div className="grid md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-4">
          <input required placeholder="Title, e.g. ALTA Survey – Texas" value={form.title} onChange={(e) => set("title")(e.target.value)}
            className={inputCls} data-testid="portfolio-upload-title-input" />
        </div>
        <div className="md:col-span-4"><ServiceSelect value={form.service} onChange={set("service")} testId="portfolio-upload-service-select" /></div>
        <div className="md:col-span-2"><input placeholder="State" value={form.state} onChange={(e) => set("state")(e.target.value)} className={inputCls} data-testid="portfolio-upload-state-input" /></div>
        <div className="md:col-span-2"><input type="number" placeholder="Year" value={form.year} onChange={(e) => set("year")(e.target.value)} className={inputCls} data-testid="portfolio-upload-year-input" /></div>
      </div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 text-sm text-zinc-700">
          <HomeSwitch checked={form.show_on_home} onChange={set("show_on_home")} testId="portfolio-upload-home-switch" />
          Show on home page slider
        </div>
        <button disabled={busy} className="h-11 px-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium disabled:opacity-60 hover:bg-[#0044FF] transition-colors" data-testid="portfolio-upload-submit-btn">
          {busy ? <><CircleNotch size={16} className="animate-spin" /> {progress < 100 ? `Uploading ${progress}%` : "Processing…"}</> : "Upload sample"}
        </button>
      </div>
    </form>
  );
}
